import { useEffect, useId, useRef, useState } from 'react';
import { useT } from '../../i18n';
import { searchClubs, type ClubSuggestion } from '../../lib/api/clubs';

// ─────────────────────────────────────────────────────────────────────
// CHOOSING A CLUB BY ITS NAME (#742).
//
// THE FIELD THAT WANTED A SLUG. Three screens asked somebody to name a club and
// sent what was typed straight through as one, so «Smaalenene Padelklubb» found
// nothing and the only way in was to guess «smaalenene-padelklubb». Reported
// from the field, on the registration screen.
//
// ONE COMPONENT FOR ALL THREE, because they had one bug three times. A pattern
// copied into three files is three places to get the debounce, the abort, the
// blur timing and the «typed it by hand» fallback slightly differently — and
// the one that drifts is the one nobody opens.
//
// ── A NAME IS NOT A SLUG, AND TYPING ONE IS NOT CHOOSING (#744 review) ──
//
// The first version forwarded whatever was typed, lowercased, as though it were
// a slug. So the ORIGINAL BUG SURVIVED on the exact path that produced it:
// type «Smaalenene Padelklubb», ignore the dropdown, press the button, and
// `smaalenene padelklubb` goes to the server — which answers 202 and sends
// nothing, because on the sign-in door a club that does not exist and an
// address that is not a member there are deliberately the same answer. The
// picker had made the bug harder to hit without removing it.
//
// So this distinguishes RESOLVED from TYPED:
//
//   a picked suggestion            resolved — its slug.
//   text that is already SLUG-SHAPED   resolved — somebody was given one, is
//                                      offline, or has a club the directory
//                                      does not list. That fallback matters
//                                      and is kept.
//   anything else (spaces, capitals…)  NOT RESOLVED. `onChange('')`, the form
//                                      cannot be submitted, and the screen says
//                                      to pick from the list.
//
// REFUSING IS STRICTLY BETTER THAN SENDING IT. A name was never a usable slug —
// it failed before and it fails now — but it used to fail silently, minutes
// later, as a code that never arrived.
//
// ── AND IT IS OPERABLE WITHOUT A MOUSE ────────────────────────────
//
// The first version bound selection to `onMouseDown` alone, which is the
// primary path nowhere: a touch screen in the Android WebView is the main way
// this is used, and a keyboard is how it is used accessibly. Arrow keys move
// the active option, Enter takes it, Escape closes the list — and Enter is
// swallowed while the list is open, or it would submit the form with an
// unresolved club underneath.
// ─────────────────────────────────────────────────────────────────────

/** Long enough that a fast typist makes one request, not six. */
const DEBOUNCE_MS = 200;

/**
 * What the server will accept as a slug — the shape `clubDirectory` matches and
 * `/onboarding/clubs/:slug/join-request` looks up.
 *
 * Deliberately NOT a validity check: it says «this could be a slug», never
 * «this club exists». The directory answers the second question and a person
 * offline gets no answer at all, which is the case this exists for.
 */
const SLUG_SHAPED = /^[a-z0-9][a-z0-9-]*$/;

export interface ClubPickerProps {
  /** The RESOLVED slug — '' when what is in the box is not one. */
  value: string;
  /** Called with a slug, or with '' when nothing is resolved. */
  onChange: (slug: string) => void;
  disabled?: boolean;
}

export function ClubPicker({ value, onChange, disabled }: ClubPickerProps) {
  const t = useT();
  const listId = useId();
  const [text, setText] = useState(value);
  const [matches, setMatches] = useState<ClubSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  /** Which option the keyboard is on; -1 is «none». */
  const [active, setActive] = useState(-1);
  // What the person picked, so the box can show «Smaalenene Padelklubb» while
  // the form holds `smaalenene-padelklubb`.
  const picked = useRef<ClubSuggestion | null>(null);

  useEffect(() => {
    const typed = text.trim();
    // ALREADY CHOSEN. Re-querying what was picked would reopen the list under
    // somebody who has finished with it.
    if (picked.current && picked.current.name === typed) return undefined;
    if (typed.length < 2) {
      setMatches([]);
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      void searchClubs(typed, controller.signal).then((found) => {
        // AN ABORTED LOOKUP ANSWERS `[]`, NOT A REJECTION (#744 re-review).
        // `searchClubs` never throws — a failed lookup is an empty list, which
        // is right for the caller and wrong for this one: a cancelled request
        // resolves a moment later and would clear a list the NEXT query has
        // already filled. Narrow, but the symptom is suggestions vanishing
        // under somebody mid-choice, which is unexplainable from the outside.
        if (controller.signal.aborted) return;
        setMatches(found);
        setActive(-1);
        setOpen(found.length > 0);
      });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      // THE IN-FLIGHT ONE IS ABANDONED, so a slow answer for «sma» cannot
      // land after the answer for «smaalenene» and replace it.
      controller.abort();
    };
  }, [text]);

  const choose = (club: ClubSuggestion) => {
    picked.current = club;
    setText(club.name);
    setMatches([]);
    setOpen(false);
    setActive(-1);
    onChange(club.slug);
  };

  const retype = (next: string) => {
    picked.current = null;
    setText(next);
    const typed = next.trim().toLowerCase();
    // ONLY IF IT COULD BE A SLUG. Anything else is unresolved, and the form
    // holding '' is what stops it being sent as one.
    onChange(SLUG_SHAPED.test(typed) ? typed : '');
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setActive(-1);
      return;
    }
    if (!open || matches.length === 0) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((i) => (i + step + matches.length) % matches.length);
      return;
    }
    if (e.key === 'Enter') {
      // SWALLOWED EVEN WHEN NOTHING IS ACTIVE. Enter inside an open list means
      // «take this», and letting it through would submit the form with an
      // unresolved club underneath — the failure this whole change is about.
      e.preventDefault();
      const chosen = matches[active === -1 ? 0 : active];
      if (chosen) choose(chosen);
    }
  };

  const unresolved = text.trim() !== '' && value === '';

  return (
    <div className="relative">
      <input
        type="text"
        value={text}
        onChange={(e) => retype(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => setOpen(matches.length > 0)}
        // A CLICK ON A SUGGESTION BLURS THE INPUT FIRST, so closing on blur
        // immediately would close the list before the click landed.
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
        className="input w-full"
        placeholder={t('clubPicker.placeholder')}
        disabled={disabled}
      />
      {open && (
        <ul
          id={listId}
          role="listbox"
          // `card-elev` rather than a hand-written background: this floats OVER
          // the form, so a transparent one shows the field beneath it through
          // the suggestions — which is exactly what the first version did.
          className="card-elev absolute z-20 left-0 right-0 mt-1 overflow-hidden max-h-56 overflow-y-auto"
        >
          {matches.map((club, i) => (
            <li key={club.slug} id={`${listId}-${i}`} role="option" aria-selected={i === active}>
              <button
                type="button"
                // BOTH, and for different inputs. `onMouseDown` fires before
                // the input's blur closes the list, which is what makes a
                // mouse work; `onClick` is what a tap and an assistive
                // activation produce. `preventDefault` on the first stops the
                // blur, so the second is not racing a closed list.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(club)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-black/5"
              >
                <span className="block">{club.name}</span>
                <span className="block text-[11px] text-ink-3 font-mono">{club.slug}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {unresolved && !open && (
        // SAID OUT LOUD, because the alternative is a disabled button with no
        // explanation — and on the sign-in screen the person would otherwise
        // be left guessing at exactly the moment the old bug used to send them
        // away to wait for nothing.
        <p className="text-xs text-ink-3 mt-1">{t('clubPicker.pickOne')}</p>
      )}
    </div>
  );
}
